/**
 * Module Host Component
 *
 * Container for running AOS modules. Displays module status,
 * provides controls, and will host module UI rendering.
 */
import { useState } from 'react';
import type { InstalledModule } from '../hooks/useModules';
import './ModuleHost.css';

interface ModuleHostProps {
  /** List of installed modules */
  installedModules: InstalledModule[];
  /** List of running module IDs */
  runningModules: string[];
  /** Run a module */
  onRun: (manifestHash: string) => void;
  /** Stop a module */
  onStop: (moduleId: string) => void;
  /** Uninstall a module */
  onUninstall: (manifestHash: string) => void;
  /** Close the module host */
  onClose: () => void;
}

export function ModuleHost({
  installedModules,
  runningModules,
  onRun,
  onStop,
  onUninstall,
  onClose,
}: ModuleHostProps) {
  const [activeTab, setActiveTab] = useState<'installed' | 'running'>('installed');
  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const runningCount = runningModules.length;
  const installedCount = installedModules.length;

  return (
    <div className="module-host-overlay" onClick={handleOverlayClick}>
      <div className="module-host">
        <div className="module-host-header">
          <h2>Modules</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="module-host-tabs">
          <button
            className={`tab ${activeTab === 'installed' ? 'active' : ''}`}
            onClick={() => setActiveTab('installed')}
          >
            Installed ({installedCount})
          </button>
          <button
            className={`tab ${activeTab === 'running' ? 'active' : ''}`}
            onClick={() => setActiveTab('running')}
          >
            Running ({runningCount})
          </button>
        </div>

        <div className="module-host-content">
          {activeTab === 'installed' && (
            <div className="module-list">
              {installedModules.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <div className="empty-text">No modules installed</div>
                  <div className="empty-hint">
                    Install modules from the DAG to extend AOS
                  </div>
                </div>
              ) : (
                installedModules.map(module => (
                  <ModuleCard
                    key={module.manifestHash}
                    module={module}
                    isRunning={runningModules.includes(module.moduleId)}
                    isExpanded={expandedModule === module.manifestHash}
                    onToggleExpand={() =>
                      setExpandedModule(
                        expandedModule === module.manifestHash ? null : module.manifestHash
                      )
                    }
                    onRun={() => onRun(module.manifestHash)}
                    onStop={() => onStop(module.moduleId)}
                    onUninstall={() => onUninstall(module.manifestHash)}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'running' && (
            <div className="module-list">
              {runningModules.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">💤</div>
                  <div className="empty-text">No modules running</div>
                  <div className="empty-hint">
                    Start a module from the Installed tab
                  </div>
                </div>
              ) : (
                installedModules
                  .filter(m => runningModules.includes(m.moduleId))
                  .map(module => (
                    <RunningModuleCard
                      key={module.manifestHash}
                      module={module}
                      onStop={() => onStop(module.moduleId)}
                    />
                  ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Module Card Component
// ============================================================================

interface ModuleCardProps {
  module: InstalledModule;
  isRunning: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRun: () => void;
  onStop: () => void;
  onUninstall: () => void;
}

function ModuleCard({
  module,
  isRunning,
  isExpanded,
  onToggleExpand,
  onRun,
  onStop,
  onUninstall,
}: ModuleCardProps) {
  const [confirmUninstall, setConfirmUninstall] = useState(false);

  const handleUninstall = () => {
    if (confirmUninstall) {
      onUninstall();
      setConfirmUninstall(false);
    } else {
      setConfirmUninstall(true);
    }
  };

  return (
    <div className={`module-card ${isRunning ? 'running' : ''}`}>
      <div className="module-card-header" onClick={onToggleExpand}>
        <div className="module-card-info">
          <div className="module-card-name">
            {module.name}
            {isRunning && <span className="running-badge">Running</span>}
          </div>
          <div className="module-card-meta">
            <span className="module-card-id">{module.moduleId}</span>
            <span className="module-card-version">v{module.version}</span>
          </div>
        </div>
        <button className="expand-btn">
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <div className="module-card-details">
          <div className="detail-row">
            <span className="detail-label">Author:</span>
            <span className="detail-value mono">
              {module.authorPubkey.slice(0, 16)}...
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Installed:</span>
            <span className="detail-value">
              {new Date(module.installedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Trusted Signers:</span>
            <span className="detail-value">
              {module.trustedSigners.length}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Permissions:</span>
            <span className="detail-value">
              {module.grantedPermissions.length}
            </span>
          </div>

          {module.grantedPermissions.length > 0 && (
            <div className="permissions-preview">
              {module.grantedPermissions.map(perm => (
                <span key={perm} className="permission-tag">{perm}</span>
              ))}
            </div>
          )}

          <div className="module-card-actions">
            {isRunning ? (
              <button className="btn-stop" onClick={onStop}>
                Stop
              </button>
            ) : (
              <button className="btn-run" onClick={onRun}>
                Run
              </button>
            )}
            <button
              className={`btn-uninstall ${confirmUninstall ? 'confirm' : ''}`}
              onClick={handleUninstall}
              onBlur={() => setConfirmUninstall(false)}
            >
              {confirmUninstall ? 'Confirm Uninstall' : 'Uninstall'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Running Module Card
// ============================================================================

interface RunningModuleCardProps {
  module: InstalledModule;
  onStop: () => void;
}

function RunningModuleCard({ module, onStop }: RunningModuleCardProps) {
  return (
    <div className="running-module-card">
      <div className="running-module-header">
        <div className="running-indicator" />
        <div className="running-module-info">
          <div className="running-module-name">{module.name}</div>
          <div className="running-module-id">{module.moduleId}</div>
        </div>
        <button className="btn-stop-small" onClick={onStop}>
          Stop
        </button>
      </div>

      {/* Future: Module UI output area */}
      <div className="running-module-output">
        <div className="output-placeholder">
          Module is running. UI output will appear here.
        </div>
      </div>
    </div>
  );
}
